'use client';

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Produto, ProdutoTamanho } from '@/app/lib/papa/definitions';
import { Cupom } from '@/app/lib/cupom/definitions';
import { validateCouponAction } from '@/app/lib/cupom/cupomActions';

export interface CartItem {
    produto: Produto;
    tamanho: ProdutoTamanho;
    quantity: number;
    observacao?: string;
}

interface CartContextType {
    cart: CartItem[];
    addToCart: (produto: Produto, tamanho: ProdutoTamanho, quantity?: number, observacao?: string) => void;
    removeFromCart: (produtoId: string, tamanhoId: string) => void;
    updateQuantity: (produtoId: string, tamanhoId: string, newQuantity: number) => void;
    updateObservacao: (produtoId: string, tamanhoId: string, observacao: string) => void;
    clearCart: () => void;
    totalItems: number;
    totalPrice: number;
    coupon: Cupom | null;
    discount: number;
    finalPrice: number;
    applyCoupon: (code: string) => Promise<{ success: boolean; message: string; }>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function useCart() {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
}

export function CartProvider({ children }: { children: ReactNode }) {
    const [cart, setCart] = useState<CartItem[]>([]);
    const [coupon, setCoupon] = useState<Cupom | null>(null);
    const [discount, setDiscount] = useState<number>(0);

    useEffect(() => {
        try {
            const storedCart = localStorage.getItem('cartData');
            if (storedCart) {
                const { cart: savedCart, timestamp, coupon: savedCoupon } = JSON.parse(storedCart);
                const oneHour = 60 * 60 * 1000;
                if (Date.now() - timestamp < oneHour) {
                    setCart(savedCart);
                    if (savedCoupon) {
                        setCoupon(savedCoupon);
                    }
                } else {
                    localStorage.removeItem('cartData');
                }
            }
        } catch (error) {
            console.error("Failed to load cart from localStorage", error);
            localStorage.removeItem('cartData');
        }
    }, []);

    const totalPrice = cart.reduce((total, item) => {
        const price = item.tamanho.valor_desconto && item.tamanho.valor_desconto > 0
            ? item.tamanho.valor_desconto
            : item.tamanho.valor;
        return total + price * item.quantity;
    }, 0);

    useEffect(() => {
        if (coupon) {
            if (coupon.valor_minimo && coupon.valor_minimo > 0 && totalPrice < coupon.valor_minimo) {
                setDiscount(0);
            } else {
                // Identifica os itens elegíveis caso o cupom seja de produtos específicos
                const isSpecific = coupon.escopo === 'produtos_especificos' && coupon.produtos_ids && coupon.produtos_ids.length > 0;
                const eligibleItems = isSpecific
                    ? cart.filter(item => coupon.produtos_ids?.includes(item.produto.id))
                    : cart;

                if (isSpecific && eligibleItems.length === 0) {
                    setDiscount(0);
                } else {
                    const baseCalculationPrice = eligibleItems.reduce((total, item) => {
                        const price = item.tamanho.valor_desconto && item.tamanho.valor_desconto > 0
                            ? item.tamanho.valor_desconto
                            : item.tamanho.valor;
                        return total + price * item.quantity;
                    }, 0);

                    let discountAmount = 0;
                    if (coupon.tipo === 'fixo') {
                        discountAmount = coupon.valor;
                    } else if (coupon.tipo === 'percentual') {
                        discountAmount = (baseCalculationPrice * coupon.valor) / 100;
                    }
                    setDiscount(Math.min(discountAmount, baseCalculationPrice));
                }
            }
        } else {
            setDiscount(0);
        }

        if (cart.length > 0) {
            try {
                const cartData = {
                    cart: cart,
                    timestamp: Date.now(),
                    coupon: coupon
                };
                localStorage.setItem('cartData', JSON.stringify(cartData));
            } catch (error) {
                console.error("Failed to save cart to localStorage", error);
            }
        } else {
            localStorage.removeItem('cartData');
        }
    }, [cart, coupon, totalPrice]);

    const addToCart = (produto: Produto, tamanho: ProdutoTamanho, quantity = 1, observacao?: string) => {
        if (!tamanho || tamanho.visivel === false || produto.visivel === false) {
            return;
        }

        const maxEstoque = (tamanho.gerenciar_estoque !== false && tamanho.quantidade_atual !== undefined)
            ? Math.max(0, tamanho.quantidade_atual)
            : 999;

        if (maxEstoque <= 0) {
            return;
        }

        setCart(prevCart => {
            const existingItem = prevCart.find(item => item.produto.id === produto.id && item.tamanho.id === tamanho.id);
            if (existingItem) {
                const novaQtd = Math.min(existingItem.quantity + quantity, maxEstoque);
                return prevCart.map(item =>
                    item.produto.id === produto.id && item.tamanho.id === tamanho.id
                        ? { ...item, quantity: novaQtd, observacao: observacao !== undefined ? observacao : item.observacao }
                        : item
                );
            } else {
                const initialQtd = Math.min(quantity, maxEstoque);
                return [...prevCart, { produto, tamanho, quantity: initialQtd, observacao }];
            }
        });
    };

    const removeFromCart = (produtoId: string, tamanhoId: string) => {
        setCart(prevCart => prevCart.filter(item => !(item.produto.id === produtoId && item.tamanho.id === tamanhoId)));
    };

    const updateQuantity = (produtoId: string, tamanhoId: string, quantity: number) => {
        if (quantity <= 0) {
            removeFromCart(produtoId, tamanhoId);
            return;
        }
        setCart(prevCart =>
            prevCart.map(item => {
                if (item.produto.id === produtoId && item.tamanho.id === tamanhoId) {
                    const maxEstoque = (item.tamanho.gerenciar_estoque !== false && item.tamanho.quantidade_atual !== undefined)
                        ? Math.max(0, item.tamanho.quantidade_atual)
                        : 999;
                    return { ...item, quantity: Math.min(quantity, maxEstoque) };
                }
                return item;
            })
        );
    };

    const updateObservacao = (produtoId: string, tamanhoId: string, observacao: string) => {
        setCart(prevCart =>
            prevCart.map(item =>
                item.produto.id === produtoId && item.tamanho.id === tamanhoId
                    ? { ...item, observacao }
                    : item
            )
        );
    };

    const clearCart = () => {
        setCart([]);
        setCoupon(null);
        setDiscount(0);
        try {
            localStorage.removeItem('cartData');
        } catch (error) {
            console.error("Failed to clear cart from localStorage", error);
        }
    };

    const applyCoupon = async (code: string) => {
        const cartProductIds = cart.map(item => item.produto.id);
        const res = await validateCouponAction(code, totalPrice, cartProductIds);
        if (res.valid && res.cupom) {
            setCoupon(res.cupom);
            return { success: true, message: res.message || 'Cupom aplicado!' };
        } else {
            setCoupon(null);
            setDiscount(0);
            return { success: false, message: res.message || 'Cupom inválido ou expirado.' };
        }
    };

    const totalItems = cart.reduce((total, item) => total + item.quantity, 0);
    const finalPrice = totalPrice - discount;

    return (
        <CartContext.Provider value={{ cart, addToCart, removeFromCart, updateQuantity, updateObservacao, clearCart, totalItems, totalPrice, coupon, discount, finalPrice, applyCoupon }}>
            {children}
        </CartContext.Provider>
    );
}